// NodeMissionPreflightModal.jsx

import React, { useCallback, useEffect, useMemo, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Utilities
import {
  getMissionTaskPersonKey,
  getMissionTaskPersonLabel,
  MISSION_TASK_HANDLED_BY,
  normalizeMissionPeople,
  normalizeMissionTask,
} from 'missions/taskmodal/missionTaskModalUtils';
import { isMissionSendMessageTask } from 'node/missions/mission/utils/missionRunUtils';
import { readHostNodePeople } from 'node/missions/mission/utils/workflowStepNodeUtils';
import { resolvePlaybookLaunchProgress } from 'node/missions/mission/preflight/playbookLaunchProgress';

// CSS
import './NodeMissionPreflightModal.css';

// Icons
import Icon from 'svg/Icon';

// Components
import Button from 'components/button/Button';
import MemberPickerModal from 'components/memberpicker/MemberPickerModal';
import ModalButtons from 'components/modal/buttons/ModalButtons';
import ModalForm from 'components/modal/form/ModalForm';
import ModalHint from 'components/modal/hint/ModalHint';
import Modal from 'components/modal/Modal';
import ProfileStack from 'components/profilestack/ProfileStack';

/**
 * getMissionLaunchHumanTasks
 *
 * Returns people-handled template tasks that need launch-time assignees.
 *
 * @param {object|null} template - Workflow template.
 * @returns {object[]} Normalized human tasks.
 */
export function getMissionLaunchHumanTasks(template) {
  return (Array.isArray(template?.tasks) ? template.tasks : [])
    .map((task) => normalizeMissionTask(task))
    .filter((task) => (
      String(task?.key || '').trim()
      && task?.handledBy === MISSION_TASK_HANDLED_BY.PEOPLE
    ));
}

/**
 * getMissionLaunchPreflightTasks
 *
 * Returns Send Message tasks that can collect launch-time recipients.
 *
 * @param {object|null} template - Workflow template.
 * @returns {object[]} Normalized Send Message tasks.
 */
export function getMissionLaunchPreflightTasks(template) {
  return (Array.isArray(template?.tasks) ? template.tasks : [])
    .map((task) => normalizeMissionTask(task))
    .filter((task) => String(task?.key || '').trim() && isMissionSendMessageTask(task));
}

function buildPeopleProfiles(people) {
  return normalizeMissionPeople(people)
    .map((person) => {
      const key = getMissionTaskPersonKey(person);
      if (!key) return null;

      const label = getMissionTaskPersonLabel(person) || key;
      return {
        key,
        userKey: key,
        name: label,
        title: String(person?.title || '').trim() || label,
        coverUrl: person?.coverUrl || '',
        data_profile: {
          name: label,
          email: person?.email || person?.data_profile?.email || '',
        },
      };
    })
    .filter(Boolean);
}

/**
 * NodeMissionPreflightModal
 *
 * Compact launch preflight: one row per human/Send Message step. Human steps
 * default to the host record’s people and can be overridden per step.
 *
 * @param {object|null} node - Current node.
 * @param {Function} onClose - Closes the preflight flow.
 * @param {Function} onLaunch - Persists the workflow run with launch-time overrides.
 * @param {string} savingMissionKey - Template key currently being saved.
 * @param {object|null} template - Workflow template being launched.
 *
 * @returns {ReactNode} NodeMissionPreflightModal.
 */
const NodeMissionPreflightModal = React.memo(({
  node = null,
  onClose,
  onLaunch,
  savingMissionKey = '',
  template = null,
}) => {
  const { theme } = useTheme();
  const isMobile = useIsMobile();

  const [assigneesByTaskKey, setAssigneesByTaskKey] = useState({});
  const [recipientsByTaskKey, setRecipientsByTaskKey] = useState({});
  const [pickerTaskKey, setPickerTaskKey] = useState('');
  const [pickerMode, setPickerMode] = useState('assignees');
  // Live Start provisioning progress (phase + counts from mint)
  const [launchProgress, setLaunchProgress] = useState(null);

  const humanTasks = useMemo(() => getMissionLaunchHumanTasks(template), [template]);
  const sendMessageTasks = useMemo(() => getMissionLaunchPreflightTasks(template), [template]);

  const launchReady = useMemo(() => {
    const humansReady = humanTasks.every((task) => (
      normalizeMissionPeople(assigneesByTaskKey?.[task.key]).length > 0
    ));
    const messagesReady = sendMessageTasks.every((task) => Boolean(
      normalizeMissionPeople(recipientsByTaskKey?.[task.key])[0]
    ));
    return humansReady && messagesReady;
  }, [assigneesByTaskKey, humanTasks, recipientsByTaskKey, sendMessageTasks]);

  const activePickerTask = useMemo(() => {
    const taskKey = String(pickerTaskKey || '').trim();
    if (!taskKey) return null;

    const pool = pickerMode === 'recipient' ? sendMessageTasks : humanTasks;
    return pool.find((task) => task.key === taskKey) || null;
  }, [humanTasks, pickerMode, pickerTaskKey, sendMessageTasks]);

  const activePickerValue = useMemo(() => {
    if (!activePickerTask) return [];

    if (pickerMode === 'recipient') {
      const selectedRecipient = normalizeMissionPeople(
        recipientsByTaskKey?.[activePickerTask.key]
      )[0] || null;
      return selectedRecipient ? [selectedRecipient] : [];
    }

    return normalizeMissionPeople(assigneesByTaskKey?.[activePickerTask.key]);
  }, [activePickerTask, assigneesByTaskKey, pickerMode, recipientsByTaskKey]);

  useEffect(() => {
    if (!template?.key) return;

    const hostPeople = readHostNodePeople(node);
    const nextAssignees = {};
    getMissionLaunchHumanTasks(template).forEach((task) => {
      nextAssignees[task.key] = hostPeople;
    });

    setAssigneesByTaskKey(nextAssignees);
    setRecipientsByTaskKey({});
    setPickerTaskKey('');
    setPickerMode('assignees');
    setLaunchProgress(null);
  }, [node, template?.key]);

  const handleClose = useCallback(() => {
    if (Boolean(template?.key) && savingMissionKey === template.key) return;

    setAssigneesByTaskKey({});
    setRecipientsByTaskKey({});
    setPickerTaskKey('');
    setPickerMode('assignees');
    setLaunchProgress(null);
    onClose?.();
  }, [onClose, savingMissionKey, template?.key]);

  const handleAssigneesChange = useCallback((taskKey, nextPeople) => {
    const normalizedTaskKey = String(taskKey || '').trim();
    if (!normalizedTaskKey) return;

    setAssigneesByTaskKey((prev) => ({
      ...prev,
      [normalizedTaskKey]: normalizeMissionPeople(nextPeople),
    }));
  }, []);

  const handleRecipientChange = useCallback((taskKey, nextPeople) => {
    const normalizedTaskKey = String(taskKey || '').trim();
    if (!normalizedTaskKey) return;

    const nextRecipient = normalizeMissionPeople(nextPeople)[0] || null;
    setRecipientsByTaskKey((prev) => ({
      ...prev,
      [normalizedTaskKey]: nextRecipient,
    }));
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!template || !launchReady) return;

    setLaunchProgress({ phase: 'preparing', completed: 0, total: humanTasks.length });

    try {
      await onLaunch?.(template, {
        launchAssigneesByTaskKey: assigneesByTaskKey,
        launchRecipientByTaskKey: recipientsByTaskKey,
        onProgress: (nextProgress) => {
          setLaunchProgress(nextProgress || null);
        },
      });
      setLaunchProgress({ phase: 'done', completed: 0, total: 0 });
      handleClose();
    } catch (error) {
      console.error('[NodeMissionPreflightModal] Start failed:', error);
      setLaunchProgress(null);
    }
  }, [
    assigneesByTaskKey,
    handleClose,
    humanTasks.length,
    launchReady,
    onLaunch,
    recipientsByTaskKey,
    template,
  ]);

  const templateIconKey = String(template?.iconKey || '').trim() || 'account_tree';
  const isSaving = Boolean(template?.key) && savingMissionKey === template.key;
  const progressView = useMemo(
    () => (isSaving || launchProgress ? resolvePlaybookLaunchProgress(launchProgress) : null),
    [isSaving, launchProgress],
  );

  const renderPeopleTrigger = ({ profiles, emptyLabel, onClick }) => {
    const nameList = profiles
      .map((profile) => profile.title || profile.name)
      .filter(Boolean)
      .join(', ');

    return (
      <button
        className={`node-mission-preflight-modal-people${profiles.length === 0 ? ' is-empty' : ''}`}
        type="button"
        title={nameList || emptyLabel}
        onClick={onClick}
      >
        {profiles.length > 0 ? (
          <ProfileStack
            profiles={profiles}
            size={26}
            maxDisplay={4}
            overlap={7}
            allowNavigation={false}
          />
        ) : (
          <span
            className="node-mission-preflight-modal-people-empty"
            style={{ color: theme.foregroundMuted }}
          >
            <Icon
              iconKey="person_add"
              width={18}
              height={18}
              color={theme.foregroundMuted}
            />
            {emptyLabel}
          </span>
        )}
      </button>
    );
  };

  return (
    <>
      <Modal
        enableClose={!isSaving}
        height="auto"
        iconKey={templateIconKey}
        isOpen={Boolean(template)}
        maxHeight={isMobile ? '100%' : 'min(720px, 80vh)'}
        title={template ? String(template.title || '').trim() || 'Start' : 'Start'}
        width={isMobile ? '100%' : 480}
        onClose={handleClose}
      >
        <div className="node-mission-preflight-modal-form-wrap">
          <ModalForm>
            <div className="node-mission-preflight-modal-container">
              {humanTasks.length === 0 && sendMessageTasks.length === 0 ? (
                <ModalHint hint="This playbook has no steps that need assignees. Start when you are ready." />
              ) : (
                <div
                  className="node-mission-preflight-modal-copy"
                  style={{ color: theme.foregroundMuted }}
                >
                  Verify the assignees for each step:
                </div>
              )}

              <div
                className={`node-mission-preflight-modal-list${isSaving ? ' is-provisioning' : ''}`}
              >
              {humanTasks.map((task) => {
                const selectedPeople = normalizeMissionPeople(assigneesByTaskKey?.[task.key]);
                const profiles = buildPeopleProfiles(selectedPeople);

                return (
                  <div
                    className="node-mission-preflight-modal-row"
                    key={task.key}
                  >
                    <div
                      className="node-mission-preflight-modal-row-title"
                      title={task.description || task.title || 'Task'}
                    >
                      {task.title || 'Task'}
                    </div>

                    {renderPeopleTrigger({
                      emptyLabel: 'Assign',
                      onClick: () => {
                        if (isSaving) return;
                        setPickerMode('assignees');
                        setPickerTaskKey(task.key);
                      },
                      profiles,
                    })}
                  </div>
                );
              })}

              {sendMessageTasks.map((task) => {
                const selectedRecipient = normalizeMissionPeople(
                  recipientsByTaskKey?.[task.key]
                )[0] || null;
                const profiles = buildPeopleProfiles(
                  selectedRecipient ? [selectedRecipient] : []
                );

                return (
                  <div
                    className="node-mission-preflight-modal-row"
                    key={`send-${task.key}`}
                  >
                    <div
                      className="node-mission-preflight-modal-row-title"
                      title={task.title || 'Send Message'}
                    >
                      {task.title || 'Send Message'}
                    </div>

                    {renderPeopleTrigger({
                      emptyLabel: 'Recipient',
                      onClick: () => {
                        if (isSaving) return;
                        setPickerMode('recipient');
                        setPickerTaskKey(task.key);
                      },
                      profiles,
                    })}
                  </div>
                );
              })}
              </div>
            </div>
          </ModalForm>
        </div>

        {/* LAUNCH PROGRESS — pinned above footer, outside the scrolling step list */}
        {progressView ? (
          <div className="node-mission-preflight-modal-progress">
            <div
              className="node-mission-preflight-modal-progress-label"
              style={{ color: theme.foreground }}
            >
              {progressView.label}
            </div>
            <div
              className="node-mission-preflight-modal-progress-track"
              style={{ backgroundColor: theme.backgroundMuted }}
            >
              <div
                className="node-mission-preflight-modal-progress-fill"
                style={{
                  backgroundColor: theme.highlight || theme.blue,
                  width: `${progressView.percent}%`,
                }}
              />
            </div>
          </div>
        ) : null}

        <ModalButtons>
          <Button
            enabled={!isSaving}
            label="Cancel"
            onClick={handleClose}
          />

          <Button
            enabled={launchReady && !isSaving}
            iconKey="play_arrow"
            label={isSaving ? 'Starting…' : 'Start'}
            onClick={handleSubmit}
          />
        </ModalButtons>
      </Modal>

      <MemberPickerModal
        isOpen={Boolean(activePickerTask)}
        label={
          activePickerTask
            ? (pickerMode === 'recipient'
              ? `Recipient for ${activePickerTask.title}`
              : `Assignees for ${activePickerTask.title}`)
            : 'People'
        }
        node={node}
        setOpen={(nextOpen) => {
          if (!nextOpen) {
            setPickerTaskKey('');
          }
        }}
        value={activePickerValue}
        baseKey={node?.baseKey || ''}
        onChange={(nextPeople) => {
          if (!activePickerTask) return;

          if (pickerMode === 'recipient') {
            handleRecipientChange(activePickerTask.key, nextPeople);
          } else {
            handleAssigneesChange(activePickerTask.key, nextPeople);
          }
          setPickerTaskKey('');
        }}
      />
    </>
  );
});

NodeMissionPreflightModal.displayName = 'NodeMissionPreflightModal';

export default NodeMissionPreflightModal;
